import { Component, inject, OnInit } from '@angular/core';
import {
  DsAccordionModule,
  DsBoxModule,
  DsButtonModule,
  DsDatepickerModule,
  DsFormFieldModule,
  DsFormsModule,
  DsIconModule,
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
  DsModalModule,
  DsTabsModule,
  DsToastModel,
  DsToastService,
  SelectListOption,
} from '@bmw-ds/components';

import { FormsModule } from '@angular/forms';
import { DealerBiddingService } from '../../services/bidding.service';
import {
  AuctionVehicle,
  LoadVehicleForm,
} from '../../models/vehicle.model';
import { TransunionService } from '../../services/transunion.service';
import { Router } from '@angular/router';
import { RoleService } from '../../services/role.service';
import { UserStateService } from '../../services/user-state.service';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'app-all-terrain-tyres',
  imports: [
    DsAccordionModule,
    DsBoxModule,
    DsButtonModule,
    DsTabsModule,
    DsFormsModule,
    DsDatepickerModule,
    DsFormFieldModule,
    FormsModule,
    DsIconModule,
    DsModalModule,
  ],
  templateUrl: './load-vehicles.component.html',
  styleUrl: './load-vehicles.component.scss',
})
export class LoadVehiclesComponent implements OnInit {
  tyreForm = {
    mileage: '',
    vehicle: '',
    destination: '',
    startDate: '',
    endDate: '',
  };

  filteredModelOptions: { id: string; label: string }[] = [];
  disableModalButton = true;
  filtersComp: any;
  private loadingLabel: string = '';

  private readonly dbConfigService = inject(DealerBiddingService);
  private readonly transunionService = inject(TransunionService);
  private loadingAreaService = inject(DsLoadingAreaService);
  private roleService = inject(RoleService);
  private readonly userStateService = inject(UserStateService);
  private toastService = inject(DsToastService);

  addToast(tone: 'critical' | 'positive' | 'neutral', teastText: string): void {
    this.toastService.pushToast({
      id: 'error-toast',
      tone: tone,
      toastText: teastText,
    } as DsToastModel);
  }
  loadingAreaConfig: DsLoadingAreaConfiguration = { id: 'bodyLoading' };

  activeTabId: string = '';
  canLoad: boolean = false;

  async ngOnInit(): Promise<void> {
    this.canLoad = this.roleService.canLoad();
    if (!this.canLoad) {
      this.addToast('critical', 'You do not have permission to load vehicles.');
      return;
    }
    const isOpen = await firstValueFrom(this.IsSystemOpen());
    if (!isOpen) {
      this.addToast(
        'critical',
        'The Bidding System is currently closed. Please load vehicles during operating hours.'
      );
      return;
    }
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.transunionService.getVehicleTypes().subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load vehicle types'
          );
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          return;
        }
        if (response.data?.vehtypeList?.lookupType) {
          this.vehicleTypes = response.data.vehtypeList.lookupType.map(
            (type: any) => ({
              id: type.code,
              label: type.description,
              value: type.code,
            })
          );
        }
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
      error: () => {
        this.addToast('critical', 'Failed to load vehicle types');
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
    });
  }
  IsSystemOpen(): Observable<boolean> {
    return this.dbConfigService.getDbConfig().pipe(
      map((config) => {
        if (
          config.success &&
          config.data?.Opening_Time &&
          config.data?.Closing_Time
        ) {
          const openTime = config.data.Opening_Time;
          const closeTime = config.data.Closing_Time;

          const now = new Date();
          const open = new Date(now);
          const close = new Date(now);

          const [openHour, openMinute] = openTime.split(':').map(Number);
          open.setHours(openHour, openMinute, 0, 0);

          const [closeHour, closeMinute] = closeTime.split(':').map(Number);
          close.setHours(closeHour, closeMinute, 0, 0);

          return now >= open && now <= close;
        }
        return false;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }
  

  onRequestTabClick(): void {
    this.activeTabId = 'requestTab';
  }

  onOverviewTabClick(): void {
    this.activeTabId = 'overviewTab';
    this.disableModalButton = true;
  }
  private router = inject(Router);

  openAssociateDetailsModal() {
    this.router.navigate(['/stock-search']);
  }

  clearAllFilters(): void {
    if (this.filtersComp) {
      this.filtersComp.clearFilters();
    }
  }

  loadVehicleForm: LoadVehicleForm = {
    vehicleType: '',
    modelYear: '',
    brand: '',
    model: '',
    fuelType: '',
    transmissionType: '',
    variant: '',
    adgCode: '',
    vin: '',
    registrationNr: '',
    engineNr: '',
    mileage: '',
    reservePrice: '',
    estimatedReconPrice: '',
    comments: '',
    photographs: [] as File[],
    conditionReport: null as File | null,
    checks: {
      hasTwoKeySets: false,
      hasTyreThread: false,
      hasServiceHistory: false,
      hasWarranty: false,
      hasMotorPlan: false,
      isRegisteredInCustomerName: false,
      hasAccidentHistory: false,
      hasCIAComments: false,
    },
  };

  vehicleSpreadsheetFile: File | null = null;
  uploadedVehicles: any[] = [];

  // Vehicle Types dropdown options

  vehicleTypes: any[] = [];
  modelYearOptions: any[] = [];
  brandOptions: any[] = [];
  modelOptions: any[] = [];
  fuelTypeOptions: any[] = [];
  transmissionTypeOptions: any[] = [];
  variantOptions: any[] = [];

  loadingDropdown = {
    modelYear: false,
    brand: false,
    model: false,
    fuelType: false,
    transmissionType: false,
    variant: false,
  };

  onPhotographsUpload(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.loadVehicleForm.photographs = Array.from(files);
    }
  }

  onConditionReportUpload(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.loadVehicleForm.conditionReport = file;
      console.log('Condition report uploaded:', file.name);
    }
  }
  isLoadVehicleDisabled(): boolean {
    const f = this.loadVehicleForm;
    return (
      !f.vehicleType ||
      !f.modelYear ||
      !f.brand ||
      !f.model ||
      !f.fuelType ||
      !f.transmissionType ||
      !f.variant ||
      !f.adgCode ||
      !f.vin ||
      !f.registrationNr ||
      !f.engineNr ||
      !f.mileage ||
      !f.reservePrice
    );
  }
  private readonly vinRegex = /^[A-Z0-9]{17}$/;

  private normalizeLoadVehicleForm(): void {
    this.loadVehicleForm.vin = (this.loadVehicleForm.vin || '')
      .trim()
      .toUpperCase();
    this.loadVehicleForm.brand = (this.loadVehicleForm.brand || '').trim();
    this.loadVehicleForm.model = (this.loadVehicleForm.model || '').trim();
    this.loadVehicleForm.variant = (this.loadVehicleForm.variant || '').trim();
    this.loadVehicleForm.modelYear = (
      this.loadVehicleForm.modelYear || ''
    ).trim();
    this.loadVehicleForm.fuelType = (
      this.loadVehicleForm.fuelType || ''
    ).trim();
    this.loadVehicleForm.transmissionType = (
      this.loadVehicleForm.transmissionType || ''
    ).trim();
    this.loadVehicleForm.registrationNr = (
      this.loadVehicleForm.registrationNr || ''
    ).trim();
    this.loadVehicleForm.engineNr = (
      this.loadVehicleForm.engineNr || ''
    ).trim();
    this.loadVehicleForm.adgCode = (this.loadVehicleForm.adgCode || '').trim();
  }

  private exceedsNumeric15_2(value: string | number): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    const normalized = String(value).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      return true;
    }
    const [wholePart] = normalized.split('.');
    return wholePart.length > 13;
  }

  private validateLoadVehicleForm(): string | null {
    const form = this.loadVehicleForm;

    if ((form.vin || '').length != 17) {
      return 'VIN must be 17 characters.';
    }
    if ((form.brand || '').length > 50) {
      return 'Brand exceeds max length of 50 characters.';
    }
    if ((form.model || '').length > 100) {
      return 'Model exceeds max length of 100 characters.';
    }
    if ((form.variant || '').length > 100) {
      return 'Variant exceeds max length of 100 characters.';
    }
    if (!/^\d{4}$/.test(form.modelYear || '')) {
      return 'Model year must be 4 digits.';
    }
    if ((form.fuelType || '').length > 20) {
      return 'Fuel type exceeds max length of 20 characters.';
    }
    if ((form.transmissionType || '').length > 20) {
      return 'Transmission type exceeds max length of 20 characters.';
    }
    if ((form.registrationNr || '').length > 20) {
      return 'Registration number exceeds max length of 20 characters.';
    }
    if ((form.engineNr || '').length > 50) {
      return 'Engine number exceeds max length of 50 characters.';
    }
    if ((form.adgCode || '').length > 20) {
      return 'MM code exceeds max length of 20 characters.';
    }

    const mileageRaw = String(form.mileage || '').trim();
    if (!/^\d+$/.test(mileageRaw)) {
      return 'Mileage must be a whole number.';
    }
    if (mileageRaw.length > 10) {
      return 'Mileage exceeds maximum allowed digits (10).';
    }

    if (this.exceedsNumeric15_2(form.reservePrice)) {
      return 'Reserve price must be numeric with up to 13 digits before decimal and 2 decimals.';
    }
    if (
      form.estimatedReconPrice &&
      this.exceedsNumeric15_2(form.estimatedReconPrice)
    ) {
      return 'Recon price must be numeric with up to 13 digits before decimal and 2 decimals.';
    }

    return null;
  }

  async onLoadVehicle(): Promise<void> {
    this.normalizeLoadVehicleForm();
    const validationError = this.validateLoadVehicleForm();
    if (validationError) {
      this.addToast('critical', validationError);
      return;
    } else {
      const loadingArea: DsLoadingAreaConfiguration = {
        id: 'bodyLoading',
        loadingText: 'Adding Vehicle...',
      };
      this.loadingAreaService.addLoadingArea(loadingArea);
      this.dbConfigService
        .addVehicle(
          await this.mapFormToAuctionVehicle(this.loadVehicleForm, {
            cNumber: this.userStateService.userId(),
            dealerCode: localStorage.getItem('dealerCode') || '',
            dealerName: localStorage.getItem('dealerName') || '',
          })
        )
        .subscribe({
          next: (response) => {
            if (!response.success) {
              this.addToast(
                'critical', 'Failed to load vehicle.'
              );
              this.loadingAreaService.removeLoadingArea(loadingArea.id);
              return;
            }
            if (response.data === '') {
              this.addToast('positive', 'Vehicle added successfully!');
              this.resetLoadVehicleForm();
            } else {
              this.addToast('neutral', response.data || 'Failed to load vehicle.');
            }
            this.loadingAreaService.removeLoadingArea(loadingArea.id);
          },
          error: (err) => {
            this.addToast(
              'critical',
              'Failed to load vehicle: ' + (err?.error || 'Unknown error')
            );
            this.loadingAreaService.removeLoadingArea(loadingArea.id);
          },
        });
    }
  }
  // Converts a File to a base64 string
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix if you only want the base64 part
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  fileToBytes(file: File): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        resolve(new Uint8Array(arrayBuffer));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  async mapFormToAuctionVehicle(
    form: LoadVehicleForm,
    user: { cNumber: string; dealerCode: string; dealerName: string }
  ): Promise<AuctionVehicle> {
    const photographsArray = Array.isArray(form.photographs)
      ? form.photographs
      : [];

    const photographsBase64 = [];
    for (const file of photographsArray) {
      photographsBase64.push(await this.fileToBase64(file));
    }

    // Convert all files to base64 while keeping filename

    const conditionReportBytes = form.conditionReport
      ? await this.fileToBytes(form.conditionReport)
      : null;

    // Convert raw bytes to base64 string
    const conditionReportBase64 = conditionReportBytes
      ? this.uint8ArrayToBase64(conditionReportBytes)
      : '';

    const conditionReportList = conditionReportBase64
      ? [conditionReportBase64]
      : [];

    return {
      vin: form.vin,
      brand: form.brand,
      model: form.model,
      variant: form.variant,
      modelYear: form.modelYear,
      fuelType: form.fuelType,
      transmission: form.transmissionType,
      loadedByCnumber: user.cNumber,
      loadedDealerCode: user.dealerCode,
      loadedDealerName: user.dealerName,
      reg: form.registrationNr,
      mileage: String(form.mileage),
      userComment: form.comments || null,
      reservePrice: Number(form.reservePrice),
      reconPrice: Number(form.estimatedReconPrice),
      highestBid: 0,
      highestBidHolder: '',
      statusCode: 'NEW',
      adgCode: form.adgCode,
      engineNr: form.engineNr,
      loadedTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      expiryTime: '',
      guid: '',
      photographS3Keys: '',
      photographsList: photographsBase64,
      conditionReportS3Key: '',
      conditionReportList: conditionReportList,
      offerAction: '',
      keySets: form.checks.hasTwoKeySets,
      tyreThread: form.checks.hasTyreThread,
      fullServiceHistory: form.checks.hasServiceHistory,
      runningWarranty: form.checks.hasWarranty,
      motorplanServicePlan: form.checks.hasMotorPlan,
      registeredInCustomersName: form.checks.isRegisteredInCustomerName,
      involvedInAccident: form.checks.hasAccidentHistory,
      ciaComments: form.checks.hasCIAComments,
    };
  }

  // mapFilesToVehicleFiles(files: File[]): VehicleFile[] {
  //   return files.map((file) => ({
  //     XSTRING: '', // Optionally set base64 if needed
  //     FILENAME: file.name,
  //   }));
  // }
  onVehicleSpreadsheetUpload(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.vehicleSpreadsheetFile = file;
      console.log('Spreadsheet uploaded:', file.name);

      // Parse the file and preview vehicles
      this.parseSpreadsheet(file);
    }
  }

  parseSpreadsheet(file: File): void {
    // Add logic to parse CSV/Excel file
    // For now, mock data
    this.uploadedVehicles = [
      {
        vin: 'WBA3B3G54ENS12345',
        brand: 'BMW',
        model: '320d',
        year: 2024,
        registrationNr: 'ABC123GP',
        mileage: 15000,
        reservePrice: 850000,
        valid: true,
      },
      // Add more mock data or parse from file
    ];
  }

  onLoadMultipleVehicles(): void {
    if (!this.vehicleSpreadsheetFile) {
      alert('Please upload a spreadsheet first');
      return;
    }

    this.dbConfigService
      .addMultipleVehicle(
        this.vehicleSpreadsheetFile,
        'Rotshidzwa',
        'Midrand',
        '21730'
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load vehicles.'
            );
            return;
          }
          this.addToast('positive', 'Vehicles added successfully!');
          this.resetMultipleVehiclesForm();
        },
        error: (err) => {
          this.addToast(
            'critical',
            'Failed to load vehicles: ' + (err?.error || 'Unknown error')
          );
        },
      });
  }

  downloadTemplate(): void {
    const link = document.createElement('a');
    link.href = 'assets/LoadMultipleVehiclesTemplate.csv';
    link.download = 'LoadMultipleVehiclesTemplate.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  resetLoadVehicleForm(): void {
    this.loadVehicleForm = {
      vehicleType: '',
      modelYear: '',
      brand: '',
      model: '',
      fuelType: '',
      transmissionType: '',
      variant: '',
      adgCode: '',
      vin: '',
      registrationNr: '',
      engineNr: '',
      mileage: '',
      reservePrice: '',
      estimatedReconPrice: '',
      comments: '',
      photographs: [] as File[],
      conditionReport: null as File | null,
      checks: {
        hasTwoKeySets: false,
        hasTyreThread: false,
        hasServiceHistory: false,
        hasWarranty: false,
        hasMotorPlan: false,
        isRegisteredInCustomerName: false,
        hasAccidentHistory: false,
        hasCIAComments: false,
      },
    };
  }

  resetMultipleVehiclesForm(): void {
    this.vehicleSpreadsheetFile = null;
    this.uploadedVehicles = [];
  }

  formatCurrency(amount: number): string {
    return `R${amount.toLocaleString('en-ZA')}`;
  }
  onVehicleTypeChange(vehicleType: string) {
    this.loadingDropdown.modelYear = true;
    this.loadingLabel = 'Loading Model Years...';
    this.transunionService.getVehicleTypeYears(vehicleType).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load model years.'
          );
          return;
        }
        this.modelYearOptions =
          response.data?.lookupList.lookup.map((y) => ({
            id: y.description,
            label: y.description,
            value: y.description,
          })) ?? [];
        this.loadVehicleForm.modelYear = '';
        this.brandOptions = [];
        this.modelOptions = [];
        this.fuelTypeOptions = [];
        this.transmissionTypeOptions = [];
        this.variantOptions = [];
      },
      complete: () => {
        this.loadingDropdown.modelYear = false;
        this.loadingLabel = '';
      },
    });
  }

  // Model Year Change
  onModelYearChange(vehicleType: string, year: string) {
    this.loadingDropdown.brand = true;
    this.loadingLabel = 'Loading Brands...';
    this.transunionService.getVehicleTypeBrands(vehicleType, year).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load brands.'
          );
          return;
        }
        this.brandOptions =
          response.data?.lookupList.lookup.map((b) => ({
            id: b.description,
            label: b.description,
            value: b.description,
          })) ?? [];
        this.loadVehicleForm.brand = '';
        this.modelOptions = [];
        this.fuelTypeOptions = [];
        this.transmissionTypeOptions = [];
        this.variantOptions = [];
      },
      complete: () => {
        this.loadingDropdown.brand = false;
        this.loadingLabel = '';
      },
    });
  }

  // Brand Change
  onBrandChange(vehicleType: string, year: string, brand: string) {
    this.loadingDropdown.model = true;
    this.loadingLabel = 'Loading Models...';
    this.transunionService
      .getVehicleTypeModels(vehicleType, year, brand)
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load models.'
            );
            return;
          }
          this.modelOptions =
            response.data?.lookupList.lookup.map((m) => ({
              id: m.description,
              label: m.description,
              value: m.description.toUpperCase(),
            })) ?? [];
          this.loadVehicleForm.model = '';
          this.fuelTypeOptions = [];
          this.transmissionTypeOptions = [];
          this.variantOptions = [];
        },
        complete: () => {
          this.loadingDropdown.model = false;
          this.loadingLabel = '';
        },
      });
  }

  // Model Change
  onModelChange(
    vehicleType: string,
    year: string,
    brand: string,
    model: string
  ) {
    this.loadingDropdown.fuelType = true;
    this.loadingLabel = 'Loading Fuel Types...';
    this.transunionService
      .getVehicleTypeFuelTypes(vehicleType, year, brand, model.toUpperCase())
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load fuel types.'
            );
            return;
          }
          if (
            !response.data?.lookupList.lookup ||
            response.data.lookupList.lookup.length === 0
          ) {
            // If no fuel types are returned, default to Petrol and Diesel
            this.fuelTypeOptions = [
              { id: 'P', label: 'Petrol', value: 'P' },
              { id: 'D', label: 'Diesel', value: 'D' },
            ];
          } else {
            this.fuelTypeOptions =
              response.data.lookupList.lookup.map((f: any) => {
                const fuelTypeMap: { [key: string]: string } = {
                  P: 'Petrol',
                  D: 'Diesel',
                  H: 'Hybrid',
                };
                const description =
                  fuelTypeMap[f.description.toUpperCase()] || f.description;
                return {
                  id: f.description,
                  label: description,
                  value: f.description,
                };
              }) ?? [];
          }
          this.loadVehicleForm.fuelType = '';
          this.transmissionTypeOptions = [];
          this.variantOptions = [];
        },
        complete: () => {
          this.loadingDropdown.fuelType = false;
          this.loadingLabel = '';
        },
      });
  }
  // Fuel Type Change
  onFuelTypeChange(
    vehicleType: string,
    year: string,
    brand: string,
    model: string,
    fuelType: string
  ) {
    this.loadingDropdown.transmissionType = true;
    this.loadingLabel = 'Loading Transmission Types...';
    this.transunionService
      .getVehicleTypeTransmissionTypes(
        vehicleType,
        year,
        brand,
        model.toUpperCase(),
        fuelType
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load transmission types.'
            );
            return;
          }
          this.transmissionTypeOptions =
            response.data?.lookupList.lookup.map((f: any) => {
              const fuelTypeMap: { [key: string]: string } = {
                A: 'Automatic',
                M: 'Manual',
              };
              const description =
                fuelTypeMap[f.description.toUpperCase()] || f.description;
              return {
                id: f.description,
                label: description,
                value: f.description,
              };
            }) ?? [];
          this.loadVehicleForm.transmissionType = '';
          this.variantOptions = [];
        },
        complete: () => {
          this.loadingDropdown.transmissionType = false;
          this.loadingLabel = '';
        },
      });
  }

  // Transmission Type Change

  variants: SelectListOption[] | null | undefined;
  onTransmissionTypeChange(
    vehicleType: string,
    year: string,
    brand: string,
    model: string,
    fuelType: string,
    transmissionType: string
  ) {
    this.loadingDropdown.variant = true;
    this.loadingLabel = 'Loading Variants...';
    this.transunionService
      .getVehicleTypeVariants(
        vehicleType,
        year,
        brand,
        model.toUpperCase(),
        fuelType,
        transmissionType
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Failed to load variants.'
            );
            return;
          }
          this.variantOptions =
            response.data?.variantList.variant.map((v) => ({
              id: v.variantDescription,
              label: v.variantDescription,
              value: v.mmCode,
            })) ?? [];
          this.loadVehicleForm.variant = '';
        },
        complete: () => {
          this.loadingDropdown.variant = false;
          this.loadingLabel = '';
        },
      });
  }

  brands(): { id: string; label: string; value: string }[] {
    return this.brandOptions;
  }

  seriesVariants(): { id: string; label: string; value: string }[] {
    return this.variantOptions;
  }
  onVariantChange(variantDescription: string | string[] | null) {
    const description = Array.isArray(variantDescription)
      ? variantDescription[0]
      : variantDescription;

    const selectedVariant = this.variantOptions.find(
      (v) => v.id === description || v.label === description
    );
    // If your API provides adgCode, use it; otherwise, use mmCode
    this.loadVehicleForm.adgCode = selectedVariant?.value || '';
  }

  protected readonly Array = Array;
}
